import React from 'react';
import { Link } from 'react-router-dom';

import { Callout, Step } from './shared';

const AiChat: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">AI Assistant</h2>
    <p className="text-base-content/50 text-sm mb-5">Not just a chatbot — a fully wired multi-agent system.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      MFlow&apos;s AI doesn&apos;t just answer questions about your data — it can{' '}
      <strong>read, reason over, and act on</strong> your workspace on your behalf, on whichever platform your project
      lives on. A coordinator agent classifies your intent and routes each request to the specialist for that platform.
    </p>

    <h3 className="font-semibold mb-3">How it works</h3>
    <div className="rounded-xl bg-base-200 shadow-card p-4 mb-6 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          {
            icon: '🧭',
            title: 'Coordinator',
            desc: 'Classifies your intent and routes each request to the right platform agent.',
          },
          {
            icon: '🤖',
            title: 'Jira Agent',
            desc: 'Reads projects, boards, issues, sprints, and changelogs, or creates and edits Jira entities when asked. Can also message you proactively — see below.',
          },
          {
            icon: '📋',
            title: 'Trello Agent',
            desc: 'Reads and edits boards, lists, and cards on your connected Trello workspace.',
          },
          {
            icon: '🗂️',
            title: 'Standalone Agent',
            desc: "Creates, updates, and deletes tasks on MFlow's own Kanban board — always available, no connection needed. Subtasks, custom statuses, labels, and comments are managed from the board UI directly.",
          },
          {
            icon: '✨',
            title: 'Wizard Agent',
            desc: 'Turns a spec into a methodology and task breakdown, then builds it on whichever platform you pick.',
          },
        ].map((c) => (
          <div key={c.title} className="rounded-lg bg-base-100 p-3 space-y-1">
            <div className="text-lg">{c.icon}</div>
            <div className="font-semibold text-sm">{c.title}</div>
            <div className="text-xs text-base-content/60 leading-relaxed">{c.desc}</div>
          </div>
        ))}
      </div>
    </div>

    <Callout type="tip">
      The Jira agent doesn&apos;t only respond when you message it — it also checks daily whether any project has
      crossed a risk threshold and, if so, messages you proactively in your main session. Look for a toast notification
      with a <strong>View</strong> button when this happens.
    </Callout>

    <p className="text-sm text-base-content/70 mt-6 mb-6 leading-relaxed">
      Curious exactly what each agent can call under the hood?{' '}
      <Link to="/docs/ai-chat/tools" className="link link-primary">
        See the full agent tool reference
      </Link>
      .
    </p>

    <h3 className="font-semibold mt-6 mb-3">Opening a chat session</h3>
    <div className="space-y-3 mb-6">
      <Step n={1}>
        Click <strong>Ask mFlow</strong> on any dashboard page, or tap the chat icon in the sidebar. A drawer panel
        slides in from the right.
      </Step>
      <Step n={2}>Pick a suggested prompt or type your own. Press Enter to send.</Step>
      <Step n={3}>
        Watch the agent work in real time — step indicators show which sub-agent is active and what tools it is calling.
      </Step>
      <Step n={4}>
        Expand to full screen with the expand icon (top-right of drawer) to see the full session with a delivery-metrics
        strip at the top.
      </Step>
    </div>

    <h3 className="font-semibold mb-2">Pinning &amp; docking the drawer</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      By default the chat is a floating overlay that sits on top of the page. Click the pin icon in the drawer header to{' '}
      <strong>dock</strong> it instead — the page layout shrinks to make room, and the chat stays open and visible while
      you keep working. Use the position dropdown next to the pin icon to move a docked chat to the{' '}
      <strong>right</strong>, <strong>left</strong>, or <strong>bottom</strong> edge of the screen. On smaller screens a
      pinned chat always docks to the bottom, regardless of the position you last picked.
    </p>

    <h3 className="font-semibold mb-2">Context-aware sessions</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      When you open the AI from a specific dashboard page the session is automatically scoped to that context — a
      project page session knows which project you&apos;re viewing; a user stats page knows which team member. This
      means you can ask &quot;what&apos;s blocking this sprint?&quot; without specifying the project.
    </p>

    <Callout type="warning">
      Every session is isolated from every other one — including a different session on the same project. This matters
      enough to have its own page:{' '}
      <Link to="/docs/chat" className="link link-primary">
        Chat Sessions &amp; Context
      </Link>
      .
    </Callout>

    <h3 className="font-semibold mb-2">Starting a general chat</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      You don&apos;t have to pick a project before you start typing. Go to <strong>Chat</strong> in the sidebar and
      click <strong>New chat</strong> — this opens a general conversation with no project attached, and the coordinator
      picks whichever agent fits what you actually ask about.
    </p>
    <div className="space-y-3 mb-4">
      <Step n={1}>
        Ask anything — the coordinator routes it to the right agent among everything you have connected, the same as it
        would for a scoped session.
      </Step>
      <Step n={2}>
        Whenever you want the conversation to be about a specific project, click <strong>Attach project</strong> at the
        top of the chat and pick one.
      </Step>
      <Step n={3}>
        A marker appears in the conversation showing where the attachment happened. Everything you ask after that point
        is scoped to that project — earlier answers in the same thread weren&apos;t, since they came before you attached
        it.
      </Step>
    </div>

    <Callout type="tip">
      You can change which project a general chat is attached to at any point — click <strong>Change</strong> next to
      the attached project&apos;s name to pick a different one, without starting a new chat.
    </Callout>

    <Callout type="tip">
      Sessions are saved and listed under <strong>Chat</strong> in the sidebar. Resume any past session to continue a
      conversation thread — the agent remembers the full context of the session. A general chat&apos;s project
      attachment is not remembered across visits though — it only applies while you have the conversation open.
    </Callout>
  </div>
);

export default AiChat;
